import { Button, Table, addToast, showDialogConfirm } from '@octano/global-ui';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';

import Loading from '../../../../../components/info/Loading';
import SectionTitle from '../../../../../components/text/SectionTitle';

import {
  CurrenciesOverview,
  CurrencyOverviewItem,
  getCurrencies,
  syncCurrencyRate,
} from './api';
import { NewRateModal } from './components/NewRateModal';
import { RateHistoryModal } from './components/RateHistoryModal';
import { useCurrencyColumns } from './hooks/useCurrencyColumns';

const dictPrefix = 'financialPortal.settings.currencies';

export default function CurrenciesSection() {
  const { t } = useTranslation();
  const [overview, setOverview] = useState<CurrenciesOverview | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshToken, setRefreshToken] = useState(0);
  const [isNewRateModalOpen, setIsNewRateModalOpen] = useState(false);
  const [historyTarget, setHistoryTarget] =
    useState<CurrencyOverviewItem | null>(null);
  const [syncingId, setSyncingId] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await getCurrencies();
      if (cancelled) {
        return;
      }
      if (error || !data) {
        addToast({
          icon: 'error',
          color: 'danger',
          text: t(`${dictPrefix}.loadError`),
        });
      } else {
        setOverview(data);
      }
      setIsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken]);

  const handleSaved = useCallback(() => {
    setIsNewRateModalOpen(false);
    setRefreshToken((prev) => prev + 1);
  }, []);

  const runSync = useCallback(
    async (currency: CurrencyOverviewItem) => {
      setSyncingId(currency.id);
      const { error } = await syncCurrencyRate(currency.id);
      setSyncingId(null);
      if (error) {
        addToast({
          icon: 'error',
          color: 'danger',
          text: t(`${dictPrefix}.syncError`),
        });
        return;
      }
      addToast({
        icon: 'success',
        color: 'success',
        text: t(`${dictPrefix}.syncSuccess`, { code: currency.code }),
      });
      setRefreshToken((prev) => prev + 1);
    },
    [t],
  );

  const handleSyncRate = useCallback(
    (currency: CurrencyOverviewItem) => {
      showDialogConfirm({
        icon: { name: 'information', color: 'primary' },
        title: t(`${dictPrefix}.syncConfirm.title`),
        subtitle: t(`${dictPrefix}.syncConfirm.subtitle`, { code: currency.code }),
        btnConfirm: {
          text: t(`${dictPrefix}.syncConfirm.confirm`),
          onConfirm: () => runSync(currency),
        },
        btnClose: { text: t(`${dictPrefix}.syncConfirm.cancel`) },
      });
    },
    [runSync, t],
  );

  const principalColumns = useCurrencyColumns({
    showActions: false,
    onShowHistory: setHistoryTarget,
    onSyncRate: handleSyncRate,
    syncingId,
  });
  const secondaryColumns = useCurrencyColumns({
    showActions: true,
    onShowHistory: setHistoryTarget,
    onSyncRate: handleSyncRate,
    syncingId,
  });

  if (isLoading) {
    return <Loading insideCard />;
  }
  if (!overview) {
    return null;
  }

  return (
    <>
      <Row>
        <Col xs={12} lg={6}>
          <SectionTitle text={t(`${dictPrefix}.principalTitle`)} />
          <p>{t(`${dictPrefix}.principalSubtitle`)}</p>
          <Table columns={principalColumns} data={[overview.principal]} />
        </Col>
        <Col xs={12} lg={6}>
          <Row className="align-items-start">
            <Col>
              <SectionTitle text={t(`${dictPrefix}.secondaryTitle`)} />
              <p>{t(`${dictPrefix}.secondarySubtitle`)}</p>
            </Col>
            <Col xs="auto">
              <Button
                text={t(`${dictPrefix}.newQuotation`)}
                onClick={() => setIsNewRateModalOpen(true)}
                size="sm"
                className="mb-1"
                outlined
              />
            </Col>
          </Row>
          <Table columns={secondaryColumns} data={overview.secondary} />
        </Col>
      </Row>
      <NewRateModal
        isOpen={isNewRateModalOpen}
        currencies={overview.secondary}
        onClose={() => setIsNewRateModalOpen(false)}
        onSaved={handleSaved}
      />
      <RateHistoryModal
        currency={historyTarget}
        onClose={() => setHistoryTarget(null)}
      />
    </>
  );
}
